/**
 * Diff-based segmenting for the Post Stream Review (PSR) toolbar.
 *
 * Turns an `originalDoc` + forward `steps` into a flat list of reviewable
 * segments (one per detected change), shaped as `AIContentPositionEntry` so the
 * PSR toolbar can navigate, view-diff and undo/redo each change independently.
 *
 * The heavy lifting — classifying the change set (default `diffType: 'smart'`) —
 * is delegated to `editor-plugin-show-diff`'s generic `computeDiffChanges`
 * utility, so the segments always line up with what the diff overlay renders.
 * This module only owns the AI-specific *shaping*: mapping each `Change` to
 * new-document positions + the slices needed for undo/redo.
 *
 * ── Segment shape ────────────────────────────────────────────────────────────
 *   - `startPos` / `endPos` — the change's range in the NEW (post-steps / live)
 *     doc. In the review moment the live doc equals the stepped doc, so these
 *     are valid live-document positions.
 *   - `originalUserContentSlice` — `originalDoc.slice(fromA, toA)`; restored on
 *     undo via `tr.replaceRange(startPos, endPos, slice)`.
 *   - `newSlice` — `newDoc.slice(fromB, toB)`; re-applied on redo.
 *   - `kind` — `add` (nothing on the original side), `remove` (nothing on the
 *     new side) or `update` (both sides non-empty).
 *
 * ── Known limitation (v1) ────────────────────────────────────────────────────
 * For `smart` changes promoted to sentence level the original-side range is
 * approximated positionally (by sentence index). Whole-block/node promotions and
 * inline/add/remove changes use exact ranges, so undo/redo is exact for those.
 */
import {
	computeDiffChanges,
	type ComputeDiffChangesParams,
} from '@atlaskit/editor-plugin-show-diff/calculate-diff';
import type { Slice } from '@atlaskit/editor-prosemirror/model';

import type {
	AIContentPositionEntry,
	SegmentKind,
} from '../../../../pm-plugins/ai-session-context/types';

/**
 * Compute the reviewable segments for `(originalDoc, steps)`.
 *
 * Returns segments in new-document reading order (ascending `startPos`). Returns
 * an empty array when there are no steps or no detectable changes. Individual
 * changes whose ranges can't be sliced (schema edge cases) are skipped rather
 * than throwing, so one bad change never sinks the whole list. Segments are
 * returned in their AI-applied form (`isUndone` left unset); the PSR owns the
 * per-segment undo state from there.
 */
export const calculateDiffSegments = (
	params: ComputeDiffChangesParams,
): AIContentPositionEntry[] => {
	const { originalDoc } = params;
	if (!params.steps || params.steps.length === 0) {
		return [];
	}

	const { changes, newDoc } = computeDiffChanges(params);

	const maxA = originalDoc.content.size;
	const maxB = newDoc.content.size;
	const segments: AIContentPositionEntry[] = [];

	for (const change of changes) {
		// Defensively clamp to each doc's bounds — promoted `smart` changes pad
		// ranges with ±1 (block open/close tokens), which can push a change at the
		// very start/end just outside `[0, size]` and make `.slice` throw.
		const fromA = Math.max(0, Math.min(change.fromA, maxA));
		const toA = Math.max(fromA, Math.min(change.toA, maxA));
		const fromB = Math.max(0, Math.min(change.fromB, maxB));
		const toB = Math.max(fromB, Math.min(change.toB, maxB));

		// A change that is empty on BOTH sides carries no content to review.
		if (toA === fromA && toB === fromB) {
			continue;
		}

		const kind: SegmentKind = fromA === toA ? 'add' : fromB === toB ? 'remove' : 'update';

		let originalUserContentSlice: Slice;
		let newSlice: Slice;
		try {
			originalUserContentSlice = originalDoc.slice(fromA, toA);
			newSlice = newDoc.slice(fromB, toB);
		} catch {
			// Range no longer sliceable against its doc — drop this one change.
			continue;
		}

		segments.push({ startPos: fromB, endPos: toB, originalUserContentSlice, newSlice, kind });
	}

	return segments;
};
